import type { RearfootWedgeType } from './prescriptionState';

/**
 * Rearfoot wedge availability + angle rules, shared by the RearfootSection form
 * and its unit tests so both move together.
 */

/** All rearfoot wedge type values, in display order. */
export const REARFOOT_WEDGE_TYPE_VALUES: readonly RearfootWedgeType[] = [
  'none',
  'kirby_skive',
  'rearfoot_post_intrinsic',
  'rearfoot_eva_post_extrinsic',
];

/**
 * Remap pre-R34 enum keys so legacy drafts classify correctly on read. New
 * writes always use the post-R34 values, so this is read-only. Shared by the
 * RearfootSection form and the Excel exporter so both interpret legacy data the
 * same way.
 */
export function normalizeRearfootWedgeType(
  t: RearfootWedgeType | undefined,
): RearfootWedgeType | undefined {
  if (t === 'intrinsic') return 'kirby_skive';
  if (t === 'extrinsic') return 'rearfoot_eva_post_extrinsic';
  return t;
}

/**
 * Both post types — the intrinsic rearfoot post and the extrinsic EVA post —
 * share a 1-10° angle range (default 3°) and carry no separate skive depth. The
 * Kirby skive uses the 0-30° angle (default 15°) plus a depth field. Pass a
 * value through normalizeRearfootWedgeType first so legacy 'extrinsic' drafts
 * are classified as a post type too.
 */
export function isRearfootPostType(type: RearfootWedgeType | undefined): boolean {
  return (
    type === 'rearfoot_post_intrinsic' || type === 'rearfoot_eva_post_extrinsic'
  );
}

/** Angle bounds + default for the two post types. */
export const REARFOOT_POST_ANGLE = { min: 1, max: 10, default: 3 } as const;
/** Angle bounds + default for the Kirby skive. */
export const REARFOOT_SKIVE_ANGLE = { min: 0, max: 30, default: 15 } as const;

/**
 * Allowed rearfoot wedge TYPE values for a shell subtype (+ sandal mode):
 *   - EVA / TPU: no extrinsic EVA post (the post is built intrinsically).
 *   - Carbon / fibreglass: no intrinsic rearfoot post (the post is an EVA post).
 *   - PA12 / polypropylene (and any other shell): all four types.
 *   - Sandal: only None + Kirby Skive (intrinsic).
 */
export function rearfootWedgeTypesFor(
  subtype: string | null | undefined,
  isSandal: boolean,
): readonly RearfootWedgeType[] {
  if (isSandal) {
    return REARFOOT_WEDGE_TYPE_VALUES.filter(
      (t) => t === 'none' || t === 'kirby_skive',
    );
  }
  const hideIntrinsicPost =
    subtype === 'carbon_insole' || subtype === 'fibreglass_insole';
  const hideExtrinsicPost = subtype === 'eva_insole' || subtype === 'tpu_insole';
  return REARFOOT_WEDGE_TYPE_VALUES.filter(
    (t) =>
      !(hideIntrinsicPost && t === 'rearfoot_post_intrinsic') &&
      !(hideExtrinsicPost && t === 'rearfoot_eva_post_extrinsic'),
  );
}
